const normalizeComparableValue = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export const valuesMatch = (left, right) =>
  normalizeComparableValue(left) === normalizeComparableValue(right);

export const createMutationId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  // `crypto.randomUUID` is available in supported browsers. This fallback keeps
  // tests and older embedded WebViews functional while preserving UUID shape.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (token) => {
    const random = Math.floor(Math.random() * 16);
    const value = token === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const withConcurrencyMetadata = (row, mutationId = createMutationId()) => {
  const expectedVersion = Number(row?.version);
  if (!Number.isInteger(expectedVersion) || expectedVersion < 1) {
    throw new Error("Cannot mutate equipment without an authoritative version.");
  }

  return {
    ...row,
    expected_version: expectedVersion,
    mutation_id: mutationId,
  };
};

export const isVersionConflictError = (error) =>
  error?.response?.status === 409 &&
  error?.response?.data?.code === "VERSION_CONFLICT";

export const isMutationReplay = (responseBody) =>
  responseBody?.replayed === true;

export const getEquipmentTagConflictDetails = (error) => {
  if (
    error?.response?.status !== 409 ||
    error?.response?.data?.code !== "EQUIPMENT_TAG_CONFLICT"
  ) {
    return null;
  }

  const conflictingItem = error.response.data.current || null;
  return {
    code: "EQUIPMENT_TAG_CONFLICT",
    message:
      error.response.data.message ||
      "The equipment tag is already in use by an active item.",
    conflictingItem,
    conflictingTag: conflictingItem?.equipment_tag || null,
  };
};

/**
 * A restore conflict converges only when the backend says this exact target is
 * already active. In particular, EQUIPMENT_TAG_CONFLICT.current is a different
 * active item and must never be mistaken for a successful restore.
 */
export const getRestoreConvergenceItem = (error, equipmentId) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const current = error?.response?.data?.current || null;
  if (
    status !== 409 ||
    !["VERSION_CONFLICT", "ITEM_ALREADY_ACTIVE"].includes(code) ||
    !current ||
    current.is_deleted ||
    String(current.id) !== String(equipmentId)
  ) {
    return null;
  }
  return current;
};

export const isRetryableMutationError = (error) => {
  const status = Number(error?.response?.status);
  return (
    !Number.isInteger(status) ||
    status === 408 ||
    status === 425 ||
    status === 429 ||
    status >= 500
  );
};

const canonicalize = (value) => {
  if (value === undefined) return { __type: "undefined" };
  if (value === null || typeof value !== "object") return value;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(canonicalize);

  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      result[key] = canonicalize(value[key]);
      return result;
    }, {});
};

export const mutationPayloadFingerprint = (value) =>
  JSON.stringify(canonicalize(value));

const cloneMutationPayload = (payload) => {
  if (payload === undefined) return undefined;
  return JSON.parse(JSON.stringify(payload));
};

const deepFreeze = (value) => {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.values(value).forEach(deepFreeze);
  return Object.freeze(value);
};

/**
 * Return the existing immutable request for the same user intent, or create a
 * fresh request when the user-visible input changed. Generated timestamps live
 * in buildPayload, so clicking Retry cannot silently change the request hash.
 */
export const getOrCreateMutationIntent = ({
  registry,
  key,
  input,
  buildPayload,
  mutationIdFactory = createMutationId,
}) => {
  const normalizedKey = String(key);
  const fingerprint = mutationPayloadFingerprint(input);
  const existing = registry.get(normalizedKey);
  if (existing?.fingerprint === fingerprint) {
    return existing;
  }

  const mutationId = mutationIdFactory();
  const intent = Object.freeze({
    fingerprint,
    mutationId,
    payload: deepFreeze(cloneMutationPayload(buildPayload(mutationId))),
  });
  registry.set(normalizedKey, intent);
  return intent;
};

export const rememberMutationIntent = ({
  registry,
  key,
  input,
  payload,
  mutationId,
}) => {
  const intent = Object.freeze({
    fingerprint: mutationPayloadFingerprint(input),
    mutationId,
    payload: deepFreeze(cloneMutationPayload(payload)),
  });
  registry.set(String(key), intent);
  return intent;
};

export const clearMutationIntentAfterError = (registry, key, error) => {
  if (!isRetryableMutationError(error)) {
    registry.delete(String(key));
  }
};

export const classifyUpdateConflict = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  const current = error?.response?.data?.current || null;
  if (status !== 409) return null;
  if (code === "ITEM_DELETED" || (code === "VERSION_CONFLICT" && current?.is_deleted)) {
    return { kind: "remote_deleted", current };
  }
  if (code === "VERSION_CONFLICT" && !current) {
    return { kind: "remote_missing", current: null };
  }
  if (code === "VERSION_CONFLICT") {
    return { kind: "version_conflict", current };
  }
  return null;
};

/** Overlay unresolved local rows (and append rows omitted by a refreshed view). */
export const preserveRowsById = (rows, replacements) => {
  const replacementsById = new Map(
    (replacements || [])
      .filter((entry) => entry?.id !== undefined && entry?.row)
      .map((entry) => [String(entry.id), entry.row])
  );
  const seen = new Set();
  const merged = (rows || []).map((row) => {
    const key = String(row.id);
    seen.add(key);
    return replacementsById.get(key) || row;
  });
  replacementsById.forEach((row, key) => {
    if (!seen.has(key)) merged.push(row);
  });
  return merged;
};

export const alignInspectionDraft = ({ pendingItem, questions, replacementImage }) => {
  const answersByQuestion = new Map(
    (Array.isArray(pendingItem?.checklist_answers)
      ? pendingItem.checklist_answers
      : []
    ).map((entry) => [entry?.question, entry?.answer || ""])
  );
  const checklistAnswers = (Array.isArray(questions) ? questions : []).map(
    (question) => ({ question, answer: answersByQuestion.get(question) || "" })
  );
  const keepReplacementImage = Boolean(
    replacementImage?.uuid &&
      replacementImage.uuid === pendingItem?.image_uuid
  );
  return { checklistAnswers, keepReplacementImage };
};

/**
 * Rebase local field edits onto the latest server row.
 *
 * Fields changed only by the server are retained. Fields changed only locally
 * are replayed. `overlappingFields` require an explicit user choice before the
 * returned candidate may be submitted.
 */
export const mergeEquipmentConflict = ({ base, local, server, fields }) => {
  const locallyChangedFields = fields.filter(
    (field) => !valuesMatch(base?.[field], local?.[field])
  );
  const remotelyChangedFields = fields.filter(
    (field) => !valuesMatch(base?.[field], server?.[field])
  );
  const remoteChanges = new Set(remotelyChangedFields);
  const overlappingFields = locallyChangedFields.filter(
    (field) =>
      remoteChanges.has(field) &&
      !valuesMatch(local?.[field], server?.[field])
  );

  const merged = { ...server };
  locallyChangedFields.forEach((field) => {
    merged[field] = local?.[field];
  });

  return {
    merged,
    locallyChangedFields,
    remotelyChangedFields,
    overlappingFields,
  };
};

/**
 * Rebase a retained local draft onto the canonical row returned by restore.
 * This is deliberately the same three-way merge used for an ordinary version
 * conflict: unrelated server changes survive and same-field changes remain an
 * explicit user decision.
 */
export const rebaseDraftOntoRestoredItem = ({ base, local, restored, fields }) =>
  mergeEquipmentConflict({ base, local, server: restored, fields });
